import { useEffect } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Plug } from "lucide-react";
import { CompactHero } from "@/components/brand/CompactHero";
import { SectionHead, Mantra } from "@/components/brand/Card";
import { useProject, useProjectCurrentLoad } from "@/hooks/useApi";
import { SourceLoadPanel } from "@/components/projects/SourceLoadCard";
import { TargetSystemSection } from "@/components/projects/TargetSystemSection";
import { formatCount } from "@/lib/format";
import { formatDate } from "@/lib/datetime";
import { SOURCE_TYPE_SHORT, shownLoad, sourceLoadState, statePill } from "@/lib/sourceLoad";

/**
 * Connections do projeto — #428.
 *
 * Até 12/09/2026 esta tela era 100% mock: um array fixo de seis cards e um diagrama "Data flow
 * topology" que desenhava um heartbeat verde pulsante mesmo em projeto sem nenhuma conexão
 * configurada. Depois virou a casa canônica das conexões de origem e do alvo.
 *
 * Desde a #1785 a origem não é mais conexão: quem lê o sistema do cliente é o ex-kit, e o que o
 * projeto tem da origem é a carga dele. A seção de origem mostra a carga vigente (e a que está
 * chegando), e a do alvo (GROW, TargetSystemSection) segue igual, com o card grande sem nenhuma
 * conexão, os cards pequenos das que existem e o mesmo modal para cadastrar e editar.
 *
 * Deep-link por seção: os cards de origem e do GROW da Overview linkam para cá com
 * `#source-systems` / `#target-system` — cada um leva direto à própria seção, em vez de abrir
 * sempre no topo (ou, sem reset de scroll numa SPA, na posição em que a Overview estava).
 */
const SECTION_IDS = ["source-systems", "target-system"] as const;

export default function ProjectConnections() {
  const { t } = useTranslation();
  const { projectId } = useParams<{ projectId: string }>();
  const location = useLocation();
  const { data: project } = useProject(projectId);
  const { data: loads, isLoading: loadLoading, isError } = useProjectCurrentLoad(projectId);
  const state = sourceLoadState(loads, loadLoading, isError);
  const load = shownLoad(loads);

  // Reage também ao fim da leitura da carga: o painel de origem muda de altura quando ela chega
  // e empurraria #target-system para baixo, deixando quem clicou no card GROW da Overview no meio
  // da seção de origem em vez de no alvo.
  useEffect(() => {
    const id = location.hash.replace(/^#/, "");
    if (!(SECTION_IDS as readonly string[]).includes(id)) return;
    document.getElementById(id)?.scrollIntoView({ behavior: "auto", block: "start" });
  }, [location.hash, loadLoading, project?.id]);

  return (
    <div className="-m-6">
      <CompactHero
        glyph={<Plug />}
        live={state === "current"}
        overline={`${(project?.name || "PROJECT").toUpperCase()} · ${t("INTEGRATIONS")}`}
        title={t("Connections")}
        kpis={[
          {
            label: t("Source system"),
            value: project?.source_type ? SOURCE_TYPE_SHORT[project.source_type] : "—",
          },
          {
            label: t("Source snapshot"),
            value: load ? formatDate(load.snapshot_at) : t("Never"),
          },
          { label: t("Tables"), value: load ? formatCount(load.table_count) : "—" },
        ]}
      />

      <div className="p-8 space-y-7">
        <section id="source-systems">
          <SectionHead
            overline={t("SOURCE")}
            title={t("Source system")}
            tag={state === "loading" ? undefined : statePill(t, state).label}
          />
          {project && <SourceLoadPanel project={project} />}
        </section>

        <section id="target-system">
          <TargetSystemSection projectId={projectId!} />
        </section>

        <Mantra>
          CLEAN CORE
        </Mantra>
      </div>
    </div>
  );
}
